/* Firestore's REST runQuery with a field list (2026-10-28). The web SDK has no
   projection, so a getDocs on busAttendance downloads every run's whole GPS
   trail: Hidden Leaf's 1,216 runs are ~52 MB, of which the fields the console
   reads are ~3.6 MB, and Overview's figures sat at zero for ~14 s after sign-in.
   The REST API takes a `select`, runs under the same security rules with the
   signed-in user's ID token, and needs the same indexes as the SDK query. Its
   values are decoded here into what the SDK would have returned — timestamps as
   Timestamp, so every toDate() check downstream keeps working.

   Callers fall back to the SDK on any failure (see `selectOrFull`), so a
   browser or network that refuses this call only loses the speed-up. */
import { collection, getDocs, limit as limitTo, orderBy as orderByField, query, QueryConstraint, Timestamp, where, WhereFilterOp } from 'firebase/firestore';
import { auth, db } from '../../firebase';

type RestValue = Record<string, unknown>;
type Raw = Record<string, unknown>;

/** Firestore writes up to nine fractional digits ("…33.123456789Z"). Date.parse
    takes three everywhere; some browsers return NaN on more, and
    Timestamp.fromMillis(NaN) throws — which would have failed the whole read. */
export function parseRfc3339(s: string): number {
  const ms = Date.parse(s.replace(/(\.\d{3})\d+(?=Z|[+-]\d\d:\d\d$)/, '$1'));
  return Number.isFinite(ms) ? ms : 0;
}

export function fromRest(v: RestValue | undefined): unknown {
  if (!v) return null;
  if ('stringValue' in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return Number(v.doubleValue);
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue' in v) return null;
  if ('timestampValue' in v) return Timestamp.fromMillis(parseRfc3339(String(v.timestampValue)));
  if ('mapValue' in v) return fieldsFromRest((v.mapValue as { fields?: Record<string, RestValue> }).fields);
  if ('arrayValue' in v) return ((v.arrayValue as { values?: RestValue[] }).values || []).map(fromRest);
  if ('geoPointValue' in v) return v.geoPointValue;
  if ('referenceValue' in v) return v.referenceValue;
  if ('bytesValue' in v) return v.bytesValue;
  return null;
}

export function fieldsFromRest(fields: Record<string, RestValue> | undefined): Raw {
  const out: Raw = {};
  Object.entries(fields || {}).forEach(([k, v]) => { out[k] = fromRest(v); });
  return out;
}

type FilterValue = string | number | boolean | Date;
const toRest = (v: FilterValue): RestValue => {
  if (v instanceof Date) return { timestampValue: v.toISOString() };
  if (typeof v === 'string') return { stringValue: v };
  if (typeof v === 'boolean') return { booleanValue: v };
  return Number.isInteger(v) ? { integerValue: String(v) } : { doubleValue: v };
};
const REST_OP: Partial<Record<WhereFilterOp, string>> = {
  '==': 'EQUAL', '>=': 'GREATER_THAN_OR_EQUAL', '>': 'GREATER_THAN', '<=': 'LESS_THAN_OR_EQUAL', '<': 'LESS_THAN',
};

export interface SelectQuery {
  collection: string;
  filters: [string, WhereFilterOp, FilterValue][];
  orderBy?: { field: string; desc?: boolean };
  limit?: number;
  /** The fields to bring back; everything else (a trail) stays on the server. */
  fields: string[];
}
export interface Row { id: string; data: Raw }

/** The query as a REST body. Exported for the test. */
export function restBody(q: SelectQuery) {
  const filters = q.filters.map(([field, op, value]) => {
    const restOp = REST_OP[op];
    if (!restOp) throw new Error(`no REST op for ${op}`);
    return { fieldFilter: { field: { fieldPath: field }, op: restOp, value: toRest(value) } };
  });
  return {
    structuredQuery: {
      from: [{ collectionId: q.collection }],
      select: { fields: q.fields.map((fieldPath) => ({ fieldPath })) },
      ...(filters.length ? { where: filters.length === 1 ? filters[0] : { compositeFilter: { op: 'AND', filters } } } : {}),
      ...(q.orderBy ? { orderBy: [{ field: { fieldPath: q.orderBy.field }, direction: q.orderBy.desc ? 'DESCENDING' : 'ASCENDING' }] } : {}),
      ...(q.limit ? { limit: q.limit } : {}),
    },
  };
}

/** Only these fields, through REST. Throws on any failure. */
export async function runSelect(q: SelectQuery): Promise<Row[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('signed out');
  const token = await user.getIdToken();
  const project = db.app.options.projectId;
  const res = await fetch(`https://firestore.googleapis.com/v1/projects/${project}/databases/(default)/documents:runQuery`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(restBody(q)),
  });
  if (!res.ok) throw new Error(`runQuery ${res.status}`);
  const rows = (await res.json()) as Array<{ document?: { name: string; fields?: Record<string, RestValue> } }>;
  return rows.flatMap((r) => (r.document ? [{ id: r.document.name.split('/').pop() as string, data: fieldsFromRest(r.document.fields) }] : []));
}

/* After one refusal, this session goes straight to the SDK rather than paying
   for a failed round trip per bus. */
let restRefused = false;

/** The fields through REST, else the whole documents through the SDK — the
    same rows either way, the second just heavier. */
export async function selectOrFull(q: SelectQuery): Promise<Row[]> {
  if (!restRefused) {
    try {
      return await runSelect(q);
    } catch {
      restRefused = true;
    }
  }
  const constraints: QueryConstraint[] = q.filters.map(([f, op, v]) => where(f, op, v instanceof Date ? Timestamp.fromDate(v) : v));
  if (q.orderBy) constraints.push(orderByField(q.orderBy.field, q.orderBy.desc ? 'desc' : 'asc'));
  if (q.limit) constraints.push(limitTo(q.limit));
  const snap = await getDocs(query(collection(db, q.collection), ...constraints));
  return snap.docs.map((d) => ({ id: d.id, data: d.data() as Raw }));
}
